import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { WHEEL_PRIZES, WHATSAPP_LINK } from "../../lib/constants";
import { Loader2, Gift, Copy, Check } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";

const SpinWheelSection = () => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [hasSpun, setHasSpun] = useState(false);
  const [prize, setPrize] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [leadWhatsApp, setLeadWhatsApp] = useState("");
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [couponCopied, setCouponCopied] = useState(false);
  const wheelRef = useRef(null);

  const handleSpin = () => {
    if (isSpinning || hasSpun) return;
    if (!leadWhatsApp) {
      toast.error("Ingresa tu WhatsApp para girar la ruleta");
      return;
    }

    setIsSpinning(true);

    // Random prize index
    const prizeIndex = Math.floor(Math.random() * WHEEL_PRIZES.length);
    const selectedPrize = WHEEL_PRIZES[prizeIndex];

    // Calculate rotation (multiple full spins + land on prize)
    const segmentAngle = 360 / WHEEL_PRIZES.length;
    const prizeAngle = prizeIndex * segmentAngle + segmentAngle / 2;
    const totalRotation = 360 * 5 + (360 - prizeAngle) + 90; // 5 full spins + prize position

    setRotation(totalRotation);

    // After spin animation
    setTimeout(() => {
      setIsSpinning(false);
      setHasSpun(true);
      setPrize(selectedPrize);
      setLeadSubmitted(true);

      // Celebration!
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6 },
        colors: ["#C88037", "#2D1810", "#E5984A", "#25D366"],
      });

      toast.success(`¡Felicidades! Ganaste: ${selectedPrize.text}`);
    }, 4000);
  };

  const copyCoupon = () => {
    const couponCode = `URBAN${prize.text.replace(/\s/g, "").toUpperCase()}`;
    navigator.clipboard.writeText(couponCode);
    setCouponCopied(true);
    toast.success("Cupón copiado al portapapeles");
    setTimeout(() => setCouponCopied(false), 2000);
  };

  const generateWhatsAppMessage = () => {
    const couponCode = `URBAN${prize.text.replace(/\s/g, "").toUpperCase()}`;
    return encodeURIComponent(
      `¡Hola Urban Brownies! 🎉\n\nGiré la ruleta y gané: ${prize.text}\nMi cupón es: ${couponCode}\n\n¡Quiero usar mi premio!`
    );
  };

  const segmentAngle = 360 / WHEEL_PRIZES.length;

  return (
    <section
      id="ruleta"
      className="py-20 md:py-32 bg-[#2D1810] overflow-hidden"
      data-testid="spin-wheel-section"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-[#C88037] font-medium text-sm uppercase tracking-wider">
            Gira y Gana
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-2 mb-4">
            Tu suerte <span className="italic text-[#C88037]">dulce</span> te espera
          </h2>
          <p className="text-white/70 max-w-xl mx-auto">
            Deja tu WhatsApp, gira la ruleta y gana un premio instantáneo. 
            ¡Todos ganan algo delicioso!
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Wheel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative wheel-container mx-auto"
          >
            {/* Pointer */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10">
              <div className="w-0 h-0 border-l-[15px] border-r-[15px] border-t-[30px] border-l-transparent border-r-transparent border-t-[#C88037] drop-shadow-lg" />
            </div>

            {/* Wheel */}
            <motion.div
              ref={wheelRef}
              animate={{ rotate: rotation }}
              transition={{ duration: 4, ease: [0.17, 0.67, 0.12, 0.99] }}
              className="w-full h-full rounded-full overflow-hidden shadow-2xl"
              style={{ transformOrigin: "center center" }}
            >
              <svg viewBox="0 0 200 200" className="w-full h-full">
                {WHEEL_PRIZES.map((prizeItem, index) => {
                  const startAngle = index * segmentAngle - 90;
                  const endAngle = (index + 1) * segmentAngle - 90;
                  const startRad = (startAngle * Math.PI) / 180;
                  const endRad = (endAngle * Math.PI) / 180;
                  const x1 = 100 + 100 * Math.cos(startRad);
                  const y1 = 100 + 100 * Math.sin(startRad);
                  const x2 = 100 + 100 * Math.cos(endRad);
                  const y2 = 100 + 100 * Math.sin(endRad);
                  const largeArc = segmentAngle > 180 ? 1 : 0;
                  const textAngle = startAngle + segmentAngle / 2;
                  const textRad = (textAngle * Math.PI) / 180;
                  const textX = 100 + 65 * Math.cos(textRad);
                  const textY = 100 + 65 * Math.sin(textRad);

                  return (
                    <g key={prizeItem.id}>
                      <path
                        d={`M100,100 L${x1},${y1} A100,100 0 ${largeArc},1 ${x2},${y2} Z`}
                        fill={prizeItem.color}
                        stroke="#FDFBF7"
                        strokeWidth="1"
                      />
                      <text
                        x={textX}
                        y={textY}
                        fill={prizeItem.textColor}
                        fontSize="8"
                        fontWeight="bold"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        transform={`rotate(${textAngle + 90}, ${textX}, ${textY})`}
                      >
                        {prizeItem.text}
                      </text>
                    </g>
                  );
                })}
                {/* Center circle */}
                <circle cx="100" cy="100" r="15" fill="#FDFBF7" />
                <circle cx="100" cy="100" r="12" fill="#C88037" />
              </svg>
            </motion.div>

            {/* Glow effect */}
            <div className="absolute inset-0 rounded-full animate-pulse-glow pointer-events-none" />
          </motion.div>

          {/* Form/Result */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-[#422418] rounded-3xl p-6 md:p-8"
          >
            {!hasSpun ? (
              <div className="space-y-6">
                <div className="flex items-center gap-2 text-[#C88037]">
                  <Gift className="w-5 h-5" strokeWidth={1.5} />
                  <span className="font-medium">1 giro por persona</span>
                </div>

                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    Tu WhatsApp (para enviarte el premio)
                  </label>
                  <input
                    type="tel"
                    value={leadWhatsApp}
                    onChange={(e) => setLeadWhatsApp(e.target.value)}
                    placeholder="+57 310 123 4567"
                    className="w-full px-4 py-3 rounded-xl bg-[#2D1810] border border-[#C88037]/30 text-white placeholder-white/40 focus:border-[#C88037] focus:ring-1 focus:ring-[#C88037] outline-none"
                    data-testid="wheel-whatsapp-input"
                  />
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSpin}
                  disabled={isSpinning || !leadWhatsApp}
                  className={`w-full py-4 rounded-full font-bold text-lg flex items-center justify-center gap-2 transition-colors ${
                    leadWhatsApp
                      ? "bg-[#C88037] text-white hover:bg-[#E5984A]"
                      : "bg-[#5E4B3C] text-white/50 cursor-not-allowed"
                  }`}
                  data-testid="spin-wheel-btn"
                >
                  {isSpinning ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} />
                      Girando...
                    </>
                  ) : (
                    <>
                      <Gift className="w-5 h-5" strokeWidth={1.5} />
                      ¡Girar la Ruleta!
                    </>
                  )}
                </motion.button>

                <p className="text-white/50 text-xs text-center">
                  Al girar, aceptas recibir novedades deliciosas de Urban Brownies
                </p>
              </div>
            ) : (
              <div className="text-center space-y-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", bounce: 0.5 }}
                  className="w-20 h-20 bg-[#C88037] rounded-full flex items-center justify-center mx-auto"
                >
                  <Gift className="w-10 h-10 text-white" strokeWidth={1.5} />
                </motion.div>

                <div>
                  <h3 className="font-serif text-3xl text-white mb-2">¡Felicidades!</h3>
                  <p className="text-white/70">Tu premio es:</p>
                  <p className="text-[#C88037] text-2xl font-bold mt-2">{prize?.text}</p>
                </div>

                {/* Coupon */}
                <div className="bg-[#2D1810] rounded-xl p-4 flex items-center justify-between">
                  <div className="text-left">
                    <span className="text-white/50 text-xs">Tu código:</span>
                    <div className="font-mono text-xl font-bold text-[#C88037]">
                      URBAN{prize?.text.replace(/\s/g, "").toUpperCase()}
                    </div>
                  </div>
                  <button
                    onClick={copyCoupon}
                    className="p-3 bg-[#422418] rounded-xl hover:bg-[#C88037]/20 transition-colors"
                    data-testid="copy-wheel-coupon"
                  >
                    {couponCopied ? (
                      <Check className="w-5 h-5 text-green-400" strokeWidth={1.5} />
                    ) : (
                      <Copy className="w-5 h-5 text-white" strokeWidth={1.5} />
                    )}
                  </button>
                </div>

                <a
                  href={`${WHATSAPP_LINK}?text=${generateWhatsAppMessage()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-[#25D366] text-white py-4 rounded-full font-semibold hover:bg-[#20BD5A] transition-colors"
                  data-testid="claim-wheel-prize"
                >
                  Reclamar Mi Premio
                </a>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SpinWheelSection;
